import type { RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { EventBus } from 'pdfjs-dist/web/pdf_viewer.mjs';

type PdfFindMatchesEvent = {
  matchesCount?: { current?: number; total?: number };
};

type UsePdfSearchOptions = {
  eventBusRef: RefObject<EventBus | null>;
};

export function usePdfSearch({ eventBusRef }: UsePdfSearchOptions) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const isSearchOpenRef = useRef(false);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState({ current: 0, total: 0 });

  const changeSearchOpen = useCallback(
    (next: boolean | ((current: boolean) => boolean)) => {
      const current = isSearchOpenRef.current;
      const value = typeof next === 'function' ? next(current) : next;
      if (value === current) {
        return;
      }
      isSearchOpenRef.current = value;
      setIsSearchOpen(value);
    },
    []
  );

  const openSearch = useCallback(() => {
    changeSearchOpen(true);
  }, [changeSearchOpen]);

  const closeSearch = useCallback(() => {
    changeSearchOpen(false);
  }, [changeSearchOpen]);

  const toggleSearch = useCallback(() => {
    changeSearchOpen((open) => !open);
  }, [changeSearchOpen]);

  const runFind = useCallback(
    (findPrevious = false) => {
      eventBusRef.current?.dispatch('find', {
        source: null,
        type: 'again',
        query,
        caseSensitive: false,
        entireWord: false,
        highlightAll: true,
        findPrevious,
        matchDiacritics: false,
      });
    },
    [eventBusRef, query]
  );

  const handleFindMatches = useCallback((event: PdfFindMatchesEvent) => {
    const count = event.matchesCount ?? { current: 0, total: 0 };
    setMatches({ current: count.current ?? 0, total: count.total ?? 0 });
  }, []);

  const resetSearch = useCallback(() => {
    changeSearchOpen(false);
    setMatches({ current: 0, total: 0 });
  }, [changeSearchOpen]);

  return {
    isSearchOpen,
    query,
    matches,
    setQuery,
    openSearch,
    closeSearch,
    toggleSearch,
    runFind,
    handleFindMatches,
    resetSearch,
  };
}
